/**
 * Reusable marketing-consent helpers.
 *
 * Three things to know:
 *
 *  1. Channels are independent. The host form's submit handler must call
 *     getValues(root) and treat email + sms as separate yes/no signals.
 *     Never derive one from the other.
 *
 *  2. Klaviyo payload shape is opinionated. Use buildKlaviyoSubscriptions()
 *     to produce the `profile.attributes.subscriptions` field — it only
 *     includes a channel when the user actively opted in for that channel,
 *     so an un-ticked SMS box doesn't accidentally subscribe the user.
 *
 *  3. Audit metadata is required (NZ Privacy Act, GDPR, TCPA, etc). Every
 *     form posts the consent timestamp, source form ID, page URL, and the
 *     versioned text the user actually saw, as custom properties on the
 *     Klaviyo profile. getMetadata(root) returns them ready-to-merge.
 *
 * Exposed surface on window.SidekickMarketingConsent:
 *   - init(root) / initAll(scope)   auto-runs on DOMContentLoaded
 *   - getValues(root)               { emailConsent, smsConsent, phoneNumber, phoneNumberE164 }
 *   - getMetadata(root)             { source, consentTextVersion, pageUrl, timestamp,
 *                                     emailConsentText, smsConsentText }
 *   - validate(root, opts)          { ok, error }
 *   - buildKlaviyoSubscriptions(v)  Klaviyo-shaped subscriptions object or undefined
 *   - buildKlaviyoConsentProperties(v, meta)  Klaviyo custom-property dict
 *   - normalizeToE164(value)        best-effort E.164 normalization
 *   - isLooselyValidPhone(value)    forgiving phone-format check
 */
(function () {
  'use strict';

  /* E.164: leading +, then 8-15 digits. The hard validator for Klaviyo. */
  function isValidE164(value) {
    return /^\+\d{8,15}$/.test((value || '').trim());
  }

  /* Loose phone check: accepts spaces, dashes, parens, dots; needs >= 8 digits.
     Used pre-submit so we can normalize before the hard E.164 check. */
  function isLooselyValidPhone(value) {
    var trimmed = (value || '').trim();
    if (!trimmed) return false;
    var stripped = trimmed.replace(/[\s\-().]/g, '');
    return /^\+?\d{8,15}$/.test(stripped);
  }

  /* Best-effort E.164 normalization. We strip formatting characters and
     keep/insert the leading +. We can't infer a country code from a bare
     "021..." NZ number without more signal — Klaviyo will reject it and
     the host form's error path surfaces the message. */
  function normalizeToE164(value) {
    var trimmed = (value || '').trim();
    if (!trimmed) return '';
    if (/^\+\d+$/.test(trimmed)) return trimmed;
    var stripped = trimmed.replace(/[\s\-().]/g, '');
    if (!stripped) return '';
    if (stripped.charAt(0) === '+') return stripped;
    if (/^\d{10,15}$/.test(stripped)) return '+' + stripped;
    return stripped;
  }

  function init(rootEl) {
    if (!rootEl || rootEl.dataset.mcInit === '1') return;
    rootEl.dataset.mcInit = '1';

    var emailCheckbox = rootEl.querySelector('[data-mc-email-checkbox]');
    var smsCheckbox = rootEl.querySelector('[data-mc-sms-checkbox]');
    var phoneInput = rootEl.querySelector('[data-mc-phone]');

    function clearInvalid(el) {
      if (!el) return;
      el.classList.remove('is-invalid');
      el.removeAttribute('aria-invalid');
    }

    if (emailCheckbox) {
      emailCheckbox.addEventListener('change', function () { clearInvalid(emailCheckbox); });
    }
    if (smsCheckbox) {
      smsCheckbox.addEventListener('change', function () {
        clearInvalid(smsCheckbox);
        /* If the user ticks SMS but the phone field is empty, gently move
           them there. We don't tick SMS for them and we never auto-tick.
           This is just a focus assist, not a dark pattern. */
        if (smsCheckbox.checked && phoneInput && !phoneInput.value.trim()) {
          phoneInput.focus();
        }
      });
    }
    if (phoneInput) {
      phoneInput.addEventListener('input', function () { clearInvalid(phoneInput); });
    }
  }

  function resolveRoot(rootOrAncestor) {
    if (!rootOrAncestor) return null;
    if (typeof rootOrAncestor.matches === 'function' && rootOrAncestor.matches('[data-marketing-consent]')) {
      return rootOrAncestor;
    }
    if (typeof rootOrAncestor.querySelector === 'function') {
      return rootOrAncestor.querySelector('[data-marketing-consent]');
    }
    return null;
  }

  function getValues(rootOrAncestor) {
    var root = resolveRoot(rootOrAncestor);
    if (!root) {
      return { emailConsent: false, smsConsent: false, phoneNumber: '', phoneNumberE164: '' };
    }
    var emailCheckbox = root.querySelector('[data-mc-email-checkbox]');
    var smsCheckbox = root.querySelector('[data-mc-sms-checkbox]');
    var phoneInput = root.querySelector('[data-mc-phone]');
    var raw = phoneInput ? phoneInput.value.trim() : '';
    return {
      emailConsent: !!(emailCheckbox && emailCheckbox.checked),
      smsConsent: !!(smsCheckbox && smsCheckbox.checked),
      phoneNumber: raw,
      phoneNumberE164: raw ? normalizeToE164(raw) : ''
    };
  }

  function getMetadata(rootOrAncestor) {
    var root = resolveRoot(rootOrAncestor);
    if (!root) return {};
    return {
      source: root.dataset.mcSource || '',
      consentTextVersion: root.dataset.mcConsentTextVersion || '',
      pageUrl: typeof window !== 'undefined' && window.location ? window.location.href : '',
      timestamp: new Date().toISOString(),
      emailConsentText: root.dataset.mcEmailConsentText || '',
      smsConsentText: root.dataset.mcSmsConsentText || ''
    };
  }

  /* Validation rules:
     - opts.requireAtLeastOneChannel (default true): block submit unless
       the user ticked email consent OR sms consent (or both). Email and
       SMS are independent — phone-without-SMS-tick still doesn't count.
     - opts.smsValidatesPhone (default true): if SMS consent is ticked,
       the phone field must contain a loosely-valid international number.
     - opts.emailConsentRequired (default false): block submit unless email
       consent specifically is ticked. Kept for legacy callers; new forms
       should rely on requireAtLeastOneChannel instead. */
  function validate(rootOrAncestor, opts) {
    opts = opts || {};
    var root = resolveRoot(rootOrAncestor);
    if (!root) return { ok: true, error: '' };

    var emailCheckbox = root.querySelector('[data-mc-email-checkbox]');
    var smsCheckbox = root.querySelector('[data-mc-sms-checkbox]');
    var phoneInput = root.querySelector('[data-mc-phone]');

    var emailTicked = !!(emailCheckbox && emailCheckbox.checked);
    var smsTicked = !!(smsCheckbox && smsCheckbox.checked);
    var phoneRaw = phoneInput ? phoneInput.value.trim() : '';

    /* Legacy: hard-require email consent. New forms should leave this off. */
    if (opts.emailConsentRequired && !emailTicked) {
      if (emailCheckbox) {
        emailCheckbox.classList.add('is-invalid');
        emailCheckbox.setAttribute('aria-invalid', 'true');
        if (typeof emailCheckbox.focus === 'function') emailCheckbox.focus();
      }
      return { ok: false, error: 'Please tick the box to subscribe to emails.' };
    }

    /* Default: require at least one marketing channel ticked. Email-and-
       SMS are independent boxes; this catches the case where the user
       fills in details but doesn't tick anything. */
    if (opts.requireAtLeastOneChannel !== false) {
      if (!emailTicked && !smsTicked) {
        /* Paint the email checkbox as the canonical first step. We don't
           paint SMS too so the visual error is anchored to one place. */
        if (emailCheckbox) {
          emailCheckbox.classList.add('is-invalid');
          emailCheckbox.setAttribute('aria-invalid', 'true');
          if (typeof emailCheckbox.focus === 'function') emailCheckbox.focus();
        }
        return {
          ok: false,
          error: 'Please tick at least one box (email or SMS) to subscribe.'
        };
      }
    }

    /* SMS ticked but no usable phone → block. Phone alone (no SMS tick)
       is fine; the host form simply drops the phone before posting. */
    if (opts.smsValidatesPhone !== false && smsTicked) {
      if (!phoneRaw) {
        if (phoneInput) {
          phoneInput.classList.add('is-invalid');
          phoneInput.setAttribute('aria-invalid', 'true');
          if (typeof phoneInput.focus === 'function') phoneInput.focus();
        }
        return { ok: false, error: 'Please enter your phone number to receive marketing text messages.' };
      }
      if (!isLooselyValidPhone(phoneRaw)) {
        if (phoneInput) {
          phoneInput.classList.add('is-invalid');
          phoneInput.setAttribute('aria-invalid', 'true');
          if (typeof phoneInput.focus === 'function') phoneInput.focus();
        }
        return { ok: false, error: 'Please enter a valid phone number with country code (e.g. +64 21 123 4567).' };
      }
    }

    return { ok: true, error: '' };
  }

  /* Build the `subscriptions` block for Klaviyo's profile payload. Only
     includes a channel when the user actively opted in for that channel.
     If neither channel is opted in we return undefined so callers can omit
     the key from the payload entirely. */
  function buildKlaviyoSubscriptions(values) {
    var subs = {};
    if (values && values.emailConsent) {
      subs.email = { marketing: { consent: 'SUBSCRIBED' } };
    }
    if (values && values.smsConsent && values.phoneNumberE164) {
      subs.sms = { marketing: { consent: 'SUBSCRIBED' } };
    }
    if (Object.keys(subs).length === 0) return undefined;
    return subs;
  }

  /* Custom properties Klaviyo can segment / audit on. We store both the
     SUBSCRIBED/NOT_SUBSCRIBED state per channel AND the exact text version
     the user saw, so we can prove informed consent if it's ever audited.
     `Last Consent *` keys are intentionally last-write-wins so signing up
     via a second form overwrites the older audit trail. */
  function buildKlaviyoConsentProperties(values, metadata) {
    values = values || {};
    metadata = metadata || {};
    var props = {
      'Email Marketing Consent': values.emailConsent ? 'SUBSCRIBED' : 'NOT_SUBSCRIBED',
      'SMS Marketing Consent': values.smsConsent ? 'SUBSCRIBED' : 'NOT_SUBSCRIBED',
      'Last Consent Form': metadata.source || '',
      'Last Consent Text Version': metadata.consentTextVersion || '',
      'Last Consent Page URL': metadata.pageUrl || '',
      'Last Consent Timestamp': metadata.timestamp || ''
    };
    if (metadata.emailConsentText) {
      props['Last Email Consent Text'] = metadata.emailConsentText;
    }
    if (values.smsConsent && metadata.smsConsentText) {
      props['Last SMS Consent Text'] = metadata.smsConsentText;
    }
    return props;
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-marketing-consent]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  window.SidekickMarketingConsent = {
    init: init,
    initAll: initAll,
    getValues: getValues,
    getMetadata: getMetadata,
    validate: validate,
    buildKlaviyoSubscriptions: buildKlaviyoSubscriptions,
    buildKlaviyoConsentProperties: buildKlaviyoConsentProperties,
    normalizeToE164: normalizeToE164,
    isValidE164: isValidE164,
    isLooselyValidPhone: isLooselyValidPhone
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function () { initAll(); });
  document.addEventListener('shopify:block:select', function () { initAll(); });
})();
